import { formatKnockoutDisplayText } from '../../utils/knockoutInput.js';

// Texte a afficher dans .dash-label : pour les ecritures non-latines,
// reintroduit le marqueur de knockout ("*mot*") autour du mot cible -- sans
// lui, un moderateur ne peut pas voir/modifier quel mot recoit le rectangle
// (cf. dashboardCard.js).
function dashboardLabelText(text, script, target) {
  if (!script || script === 'Latn') return text;
  return formatKnockoutDisplayText(text, target);
}

function buildDashboardItem({ id, text, pseudo, lat, lng, data, script = 'Latn', knockoutSvg = null, target = null }) {
  return {
    id,
    text,
    pseudo: pseudo || '',
    lat: lat ?? null,
    lng: lng ?? null,
    data,
    script,
    knockoutSvg,
    target,
    canvas: null,
    ctx: null,
    bg: '#ffffff',
    fg: '#000000',
    tX: 0,
    rX: 0,
    ro: null,
    cardEl: null,
  };
}

function attachDashboardItem({ item, dashboardItems, dashboardList, createDashCard, updateDashCount, resizeDashMiniCanvas, startDashMiniAnim, rebuildGalerie, updateCarteMarkers }) {
  dashboardItems.unshift(item);
  // null tant que le dashboard n'a jamais été ouvert (cartes construites à
  // la demande, cf. ensureDashboardCards dans app.js).
  const li = createDashCard(item);
  if (li) dashboardList.insertBefore(li, dashboardList.firstChild);
  updateDashCount();

  requestAnimationFrame(() => {
    resizeDashMiniCanvas(item);
    startDashMiniAnim(item);
    if (typeof ResizeObserver !== 'undefined' && item.canvas) {
      item.ro = new ResizeObserver(() => resizeDashMiniCanvas(item));
      item.ro.observe(item.canvas.parentElement);
    }
  });

  rebuildGalerie();
  updateCarteMarkers();

  return li;
}

export function createRealtimeDashboardFeature({
  dashboardItems,
  dashboardList,
  createEventsStream,
  createGenerationRecord,
  prepareAnimLine,
  resolveLineData,
  loadFont,
  parseFont,
  getAnimFont,
  setAnimFont,
  createDashCard,
  updateDashCount,
  resizeDashMiniCanvas,
  startDashMiniAnim,
  stopDashMiniAnim,
  rebuildGalerie,
  updateCarteMarkers,
  refreshContributors,
  showHint,
  fetchGenerations,
}) {
  function detachItem(item) {
    stopDashMiniAnim(item);
    if (item.ro) {
      item.ro.disconnect();
      item.ro = null;
    }
    item.cardEl?.remove();
    dashboardItems.splice(dashboardItems.indexOf(item), 1);
  }

  function refreshAfterRemoval() {
    updateDashCount();
    rebuildGalerie();
    updateCarteMarkers();
    refreshContributors?.();
  }

  // Retire les punchlines supprimees en base pendant que la page etait
  // deconnectee du flux SSE -- un onglet mobile mis en arriere-plan (iOS)
  // perd la connexion, et les 'delete-punchline' emis pendant ce temps ne
  // sont jamais rejoues a la reconnexion. Sans ca, la liste locale garde des
  // phrases effacees et le Proposer les refuse comme doublons (cas reel,
  // utilisateur au Canada, 28 septembre 2026).
  async function resyncDeletions() {
    const rows = await fetchGenerations();
    const liveIds = new Set(rows.map((row) => 'db-' + row.id));
    const stale = dashboardItems.filter((item) => item.id.startsWith('db-') && !liveIds.has(item.id));
    if (stale.length === 0) return;
    stale.forEach(detachItem);
    refreshAfterRemoval();
  }

  function connectSSE() {
    const es = createEventsStream();

    // 'open' se declenche aussi a chaque reconnexion automatique d'EventSource :
    // on ignore la toute premiere ouverture (liste deja fraiche au chargement).
    let openedOnce = false;
    es.addEventListener('open', () => {
      if (openedOnce) {
        resyncDeletions().catch((error) => console.error('Resync suppressions:', error));
      }
      openedOnce = true;
    });

    es.addEventListener('delete-punchline', (event) => {
      const { id } = JSON.parse(event.data);
      const dbId = 'db-' + id;
      const item = dashboardItems.find((current) => current.id === dbId);
      if (!item) return;

      detachItem(item);
      refreshAfterRemoval();
    });

    es.addEventListener('new-punchline', async (event) => {
      const row = JSON.parse(event.data);
      const dbId = 'db-' + row.id;
      if (dashboardItems.some((current) => current.id === dbId)) return;

      const pendingItem = dashboardItems.find((current) => !current.id.startsWith('db-') && current.text === row.inputText);
      if (pendingItem) {
        pendingItem.id = dbId;
        if (pendingItem.cardEl) pendingItem.cardEl.dataset.id = dbId;
        refreshContributors?.();
        return;
      }

      try {
        const buf = await loadFont();
        if (!getAnimFont()) setAnimFont(await parseFont(buf));
        const lineData = await resolveLineData({
          script: row.script, text: row.inputText, knockoutLineData: row.knockoutLineData,
          prepareAnimLine, font: getAnimFont(),
        });
        if (!lineData) return;

        const item = buildDashboardItem({
          id: dbId,
          text: row.inputText,
          pseudo: row.pseudo,
          lat: row.lat,
          lng: row.lng,
          data: lineData,
          script: row.script || 'Latn',
          knockoutSvg: row.knockoutSvg || null,
          target: row.target || null,
        });

        attachDashboardItem({
          item,
          dashboardItems,
          dashboardList,
          createDashCard,
          updateDashCount,
          resizeDashMiniCanvas,
          startDashMiniAnim,
          rebuildGalerie,
          updateCarteMarkers,
        });
        refreshContributors?.();
      } catch (error) {
        console.error('SSE new-punchline error:', error);
      }
    });

    es.addEventListener('update-punchline', async (event) => {
      const row = JSON.parse(event.data);
      const dbId = 'db-' + row.id;
      const item = dashboardItems.find((current) => current.id === dbId);
      if (!item) return;

      const pseudo = row.pseudo || '';
      if (item.pseudo !== pseudo) {
        item.pseudo = pseudo;
        const pseudoEl = item.cardEl?.querySelector('.dash-pseudo');
        if (pseudoEl) pseudoEl.textContent = pseudo;
      }
      item.lat = row.lat ?? null;
      item.lng = row.lng ?? null;
      item.script = row.script || 'Latn';
      item.knockoutSvg = row.knockoutSvg || null;
      // Un mot cible different peut laisser inputText strictement identique
      // (memes mots, juste un autre choisi comme cible) -- textChanged seul
      // ratait alors ce cas, laissant le rectangle/l'anim affiches perimes.
      const targetChanged = (row.target || null) !== item.target;
      item.target = row.target || null;

      if (row.inputText && (row.inputText !== item.text || targetChanged)) {
        try {
          const buf = await loadFont();
          if (!getAnimFont()) setAnimFont(await parseFont(buf));
          const lineData = await resolveLineData({
            script: row.script, text: row.inputText, knockoutLineData: row.knockoutLineData,
            prepareAnimLine, font: getAnimFont(),
          });
          if (lineData) {
            stopDashMiniAnim(item);
            item.text = row.inputText;
            item.data = lineData;
            item.tX = 0;
            item.rX = 0;
            const labelEl = item.cardEl?.querySelector('.dash-label');
            if (labelEl) labelEl.textContent = dashboardLabelText(row.inputText, item.script, item.target);
            resizeDashMiniCanvas(item);
            startDashMiniAnim(item);
            rebuildGalerie();
          }
        } catch (error) {
          console.error('SSE update-punchline error:', error);
        }
      }

      updateCarteMarkers();
      refreshContributors?.();
    });

    // Aucun état local (pseudo/texte de photo) n'est mis en cache ici pour les
    // photos AR/carte, contrairement aux punchlines (dashboardItems) : on se
    // contente de redemander les collections et de reconstruire les marqueurs,
    // comme le fait déjà updateCarteMarkers() pour new/delete-punchline.
    ['new-ar-photo', 'update-ar-photo', 'delete-ar-photo', 'new-map-photo', 'update-map-photo', 'delete-map-photo']
      .forEach((eventName) => es.addEventListener(eventName, () => { updateCarteMarkers(); refreshContributors?.(); }));
  }

  async function addToDashboard(text, pseudo = '', lat = null, lng = null, knockout = null) {
    if (!text.trim()) return;
    const script = knockout?.script || 'Latn';
    const knockoutSvg = knockout?.svg || null;
    const knockoutLineData = knockout?.lineData || null;
    const target = knockout?.target || null;

    try {
      const buf = await loadFont();
      if (!getAnimFont()) setAnimFont(await parseFont(buf));
      const lineData = await resolveLineData({
        script, text, knockoutLineData, prepareAnimLine, font: getAnimFont(),
      });
      if (!lineData) return;

      const item = buildDashboardItem({
        id: Date.now() + '-' + Math.random().toString(36).slice(2),
        text,
        pseudo,
        lat,
        lng,
        data: lineData,
        script,
        knockoutSvg,
        target,
      });

      const li = attachDashboardItem({
        item,
        dashboardItems,
        dashboardList,
        createDashCard,
        updateDashCount,
        resizeDashMiniCanvas,
        startDashMiniAnim,
        rebuildGalerie,
        updateCarteMarkers,
      });

      try {
        const result = await createGenerationRecord({
          inputText: text,
          pseudo: item.pseudo,
          lat: item.lat,
          lng: item.lng,
          script,
          knockoutSvg,
          knockoutLineData,
          target,
        });

        if (result?.duplicate) {
          stopDashMiniAnim(item);
          if (item.ro) {
            item.ro.disconnect();
            item.ro = null;
          }
          li?.remove();
          dashboardItems.shift();
          updateDashCount();
          rebuildGalerie();
          showHint('hintDuplicate');
          return;
        }

        if (result?.ok && result.id) {
          item.id = 'db-' + result.id;
          li.dataset.id = item.id;
        }
      } catch (dbError) {
        console.error('Erreur enregistrement DB:', dbError);
      }
    } catch (error) {
      console.error('Dashboard add error:', error);
    }
  }

  return {
    connectSSE,
    addToDashboard,
    resyncDeletions,
  };
}
